import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { getBannerConfig } from "./ViewRequestStatusMeta";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens, {
  viewSignRequestCompactTokens,
} from "./ViewSignRequest.tokens";
import { AlertCircleGlyph, CheckGlyph } from "./ViewRequestIcons";

const styles = viewSignRequestStyles;
const { banner } = viewSignRequestTokens;
const { banner: compactBanner } = viewSignRequestCompactTokens;

interface SignRequestBannerProps {
  status?: string;
  isSigned: boolean;
  /** Formatted date of the completion event, appended to the success copy. */
  completedOn?: string;
  /** Optional download action for completed requests. */
  onDownload?: () => void;
  downloading?: boolean;
  canDownload?: boolean;
}

/**
 * Contextual strip under the header card summarising the request outcome
 * (completed / declined / voided / expired).
 */
const SignRequestBanner: React.FC<SignRequestBannerProps> = ({
  status,
  isSigned,
  completedOn,
  onDownload,
  downloading,
  canDownload,
}) => {
  const theme = useTheme();
  // Tablet has no room for the full label; the phone runs it full width.
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const cfg = getBannerConfig(status, isSigned, completedOn);

  if (!cfg.show) return null;

  const glyphSx = {
    width: banner.iconGlyphSize,
    height: banner.iconGlyphSize,
    color: cfg.isError ? banner.errorIcon : banner.successIcon,
  };

  return (
    <Box sx={styles.banner(cfg.isError)}>
      <Box sx={styles.bannerLead}>
        <Box sx={styles.bannerIcon}>
          {cfg.isError ? (
            <AlertCircleGlyph sx={glyphSx} />
          ) : (
            <CheckGlyph sx={glyphSx} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" sx={styles.bannerTitle(cfg.isError)}>
            {cfg.title}
          </Typography>
          <Typography component="div" sx={styles.bannerSub}>
            {cfg.sub}
          </Typography>
        </Box>
      </Box>
      {cfg.showDownload && canDownload && onDownload && (
        <Button
          disableRipple
          type="button"
          onClick={onDownload}
          disabled={downloading}
          aria-busy={downloading}
          sx={styles.bannerButton}
        >
          {downloading
            ? "Downloading..."
            : isTablet
              ? compactBanner.buttonLabel[2]
              : compactBanner.buttonLabel[1]}
        </Button>
      )}
    </Box>
  );
};

export default SignRequestBanner;
